import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  inject,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import {
  GotoApiService,
  GotoProjectInviteLinkResponse,
  PublicApiGotoResponse,
  ShareProjectBoardGotoResponse,
} from '../../shared/services/api/goto-api.service';
import { ReplaySubject, takeUntil } from 'rxjs';
import { SelectedWorkspaceStore } from '../../stores/selected-workspace.store';
import { DefaultUser, IUser } from '../../interfaces';
import { GuestAccessStore } from '../../stores/guest-store';
import { GlobalLoaderService } from '../../global-loader.service';
import { TokenService } from '../../pages/auth/services/token.service';
import {
  CurrentPeriod, PaymentState,
  ProjectFromList,
  SubscriptionType,
} from '../../pages/workspace/features/create-workspace/services/workspace-api.service';

@Component({
  selector: 'app-goto-proxy',
  standalone: true,
  templateUrl: './goto-proxy.component.html',
  styleUrl: './goto-proxy.component.less',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class GotoProxyComponent implements OnInit, OnDestroy {
  private ar = inject(ActivatedRoute);
  private gotoApi = inject(GotoApiService);
  private selectedWorkspaceStore = inject(SelectedWorkspaceStore);
  private token = inject(TokenService);
  private router = inject(Router);
  private cdr = inject(ChangeDetectorRef);
  private loader = inject(GlobalLoaderService);

  private destroy$ = new ReplaySubject<void>();
  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  ngOnInit() {
    const gotoId = this.ar.snapshot.params['goto_uuid'];

    this.gotoApi
      .checkGotoPermission(gotoId)
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        (it) => {
          switch (it.type) {
            case 'project_personal_invite_link':
              this.handleProjectInviteLinkGoto(it);
              break;
            case 'project_board_invite_link':
              this.handleSharedBoardLinkGoto(it);
              break;
            case 'perare_api_plot_link':
              this.handlePublicApiGoto(it);
              break;

            // TODO: add default with error behavior
          }
        },
        (err) => {
          this.loader.errorMessage.set('Your link is expired or invalid. Please, request new link');
          this.cdr.detectChanges();
        },
      );
  }

  private handleSharedBoardLinkGoto(it: ShareProjectBoardGotoResponse) {
    if (!it.token) {
      const tempUser = this.selectedWorkspaceStore.getUserSync();

      GuestAccessStore.patchState({
        enabled: true,
        user: tempUser,
        token: this.token.getToken(),
      });
      this.router.navigate(['project', it.data.projectId, 'board', it.data.boardId]);
      this.loader.isLoading.set(false);
      return;
    }

    const tempUser: IUser = { ...DefaultUser, ...it.data.userInfo };
    GuestAccessStore.patchState({ enabled: true, user: tempUser, token: it.token });
    this.router.navigate(['project', it.data.projectId, 'board', it.data.boardId]);
    this.loader.isLoading.set(false);
  }

  private async handleProjectInviteLinkGoto(it: GotoProjectInviteLinkResponse) {
    const inviteString = `${it.data.projectOwnerName} invites you in '${it.data.projectName}' Project. Would you like to accept invitation?`;

    const res = window.confirm(inviteString);

    if (res) {
      this.gotoApi
        .checkGotoPermission(it.id, {
          verdict: 'accepted',
        })
        .pipe(takeUntil(this.destroy$))
        .subscribe((it) => {
          this.router.navigate(['projects']);
        });
    }
  }

  private handlePublicApiGoto(it: PublicApiGotoResponse) {
    let tempUser = this.selectedWorkspaceStore.getUserSync();
    if (tempUser) {
      GuestAccessStore.patchState({
        enabled: true,
        user: tempUser,
        token: this.token.getToken(),
        isPublicApiGuest: true,
      });
    } else {
      tempUser = { ...DefaultUser, ...it.data.userInfo };
      GuestAccessStore.patchState({ enabled: true, user: tempUser, token: it.token, isPublicApiGuest: true });
    }

    const period: CurrentPeriod = {
      subscriptionType: SubscriptionType.FREE,
      subscriptionPeriod: null,
      expireDate: null,
      paymentState: PaymentState.APPLIED,
      paymentLink: null,
      seatCount: 1,
    };

    const project: ProjectFromList = {
      id: it.data.projectId,
      name: 'Project',
      balance: 0,
      subscriptionType: SubscriptionType.FREE,
      upgradeCurrentPeriod: null,
      plannedPeriod: null,
      currentPeriod: period,
      needPayNextPeriod: false,
      needPayNextPeriodCase: 'actual',
      needPayNextPeriodDiff: 0,
      ownerUuid: '',
      ownerName: '',
      seats: {
        assigned: [],
        unassigned: [],
      },
      avatar: null,
      previousInvoices: [],
    };

    this.selectedWorkspaceStore.setWorkspace(project);

    this.router.navigate(['project', it.data.projectId, 'board', it.data.boardId], { state: it });
    this.loader.isLoading.set(false);
  }
}
